/**
 * Is there room for the desktop shell?
 *
 * A media query rather than a stored preference, because that is the question
 * being asked: "is there room". Somebody who rotates a phone has not changed
 * their mind about anything, and a setting would have to be un-set when they
 * rotated back.
 *
 * 820px is above every phone in portrait and below a landscape tablet, which is
 * the boundary that matters — a landscape iPad has room for the grid, the
 * dividers and the strip, and a portrait phone has room for none of them. See
 * [mobile.md](../../../../docs/features/mobile.md).
 *
 * The subscription is `matchMedia`, not a resize listener: the browser fires it
 * on the transition rather than on every pixel of a drag, so a desktop window
 * being resized costs two renders instead of two hundred.
 */

import { useEffect, useState } from "react";

/**
 * The mobile shell: narrow, short with a touch pointer, or a tablet.
 *
 * The second clause is a phone on its side. Width alone sent every modern
 * iPhone in landscape (852–956 wide, 393–440 tall) to the desktop shell — a
 * mouse layout in less height than the phone shell's own chart needs. Short
 * with a mouse is a desktop window dragged small, and keeps the shell it had;
 * every iPad is taller than 500 either way up, and is the third clause. See
 * [mobile.md](../../../../docs/features/mobile.md).
 */
/**
 * A tablet: touch, and room both ways — an iPad either way up, but not an iPad
 * in narrow Split View, which is a phone's width. It gets the touch shell with
 * All and Full (native-apps.md §9.3–9.4) instead of the desktop Workspace. A
 * desktop with a touch screen is not one: its primary pointer is the mouse.
 */
export const TABLET_QUERY = "(pointer: coarse) and (min-width: 600px) and (min-height: 600px)";

export const NARROW_QUERY =
  "(max-width: 820px), (max-height: 500px) and (pointer: coarse), " + TABLET_QUERY;

/**
 * Whether the viewport is narrow enough for the mobile shell.
 *
 * `false` where `matchMedia` is missing — jsdom without a stub, and any
 * non-browser host. The desktop shell is the one that has always rendered, so
 * an unknown environment gets the answer that changes nothing.
 */
export function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(matchesNarrow);

  useEffect(() => {
    const query = globalThis.matchMedia?.(NARROW_QUERY);
    if (!query) return;
    // Read once on mount as well as subscribing: `useState`'s initialiser ran
    // during the first render, and a viewport that changed between then and the
    // effect — a rotation during hydration — would otherwise stick.
    setNarrow(query.matches);
    const onChange = (event: MediaQueryListEvent) => setNarrow(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return narrow;
}

/** Any media query, followed as it changes. `false` where `matchMedia` is missing. */
export function useMediaQuery(query: string): boolean {
  const read = () => globalThis.matchMedia?.(query).matches ?? false;
  const [on, setOn] = useState(read);
  useEffect(() => {
    const mq = globalThis.matchMedia?.(query);
    if (!mq) return;
    setOn(mq.matches);
    const change = (event: MediaQueryListEvent) => setOn(event.matches);
    mq.addEventListener("change", change);
    return () => mq.removeEventListener("change", change);
  }, [query]);
  return on;
}

/** The current answer, outside React. Exported for the test's sake. */
export function matchesNarrow(): boolean {
  return globalThis.matchMedia?.(NARROW_QUERY).matches ?? false;
}
